import { createContext, useContext, useEffect, useState, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

function fmtError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [entitlements, setEntitlements] = useState(null);

  const loadEntitlements = useCallback(async () => {
    try {
      const r = await fetch(`${API}/billing/me`, { credentials: "include" });
      if (r.ok) setEntitlements(await r.json());
      else setEntitlements(null);
    } catch {
      setEntitlements(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          setUser(d.user);
          await loadEntitlements();
        } else setUser(false);
      } catch {
        setUser(false);
      }
    })();
  }, [loadEntitlements]);

  const login = useCallback(async (email, password) => {
    const r = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(fmtError(d.detail));
    setUser(d.user);
    await loadEntitlements();
    return d.user;
  }, [loadEntitlements]);

  const register = useCallback(async (name, email, password) => {
    const r = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name, email, password }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(fmtError(d.detail));
    setUser(d.user);
    await loadEntitlements();
    return d.user;
  }, [loadEntitlements]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } finally {
      setUser(false);
      setEntitlements(null);
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const r = await fetch(`${API}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(fmtError(d.detail));
    return true;
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    const r = await fetch(`${API}/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(fmtError(d.detail));
    return true;
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const r = await fetch(`${API}/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(fmtError(d.detail));
    return true;
  }, []);

  const resendVerification = useCallback(async () => {
    const r = await fetch(`${API}/auth/resend-verification`, {
      method: "POST",
      credentials: "include",
    });
    if (!r.ok) throw new Error("Could not resend verification email.");
    return true;
  }, []);

  const hasFeature = useCallback(
    (f) => !!entitlements && Array.isArray(entitlements.features) && entitlements.features.includes(f),
    [entitlements]
  );

  return (
    <AuthContext.Provider
      value={{ user, entitlements, hasFeature, refreshEntitlements: loadEntitlements, login, register, logout,
               forgotPassword, resetPassword, verifyEmail, resendVerification }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
